import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import MuxPlayer from '@mux/mux-player-react'
import type MuxPlayerElement from '@mux/mux-player'
import {
  assignmentService,
  clientService,
  type AssignmentWithProgress,
  type ProgramItemWithProgress,
} from '../../services'
import { Button, LoadingScreen } from '../../components/common'
import { useToast } from '../../components/common/Toast'
import { muxConfig } from '../../config/mux.config'

export function ClientVideoPage() {
  const { token, id: assignmentId, itemId } = useParams<{
    token: string
    id: string
    itemId: string
  }>()
  const navigate = useNavigate()
  const toast = useToast()

  const [assignment, setAssignment] = useState<AssignmentWithProgress | null>(null)
  const [currentItem, setCurrentItem] = useState<ProgramItemWithProgress | null>(null)
  const [clientId, setClientId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [watchedSeconds, setWatchedSeconds] = useState(0)
  const [isCompleted, setIsCompleted] = useState(false)
  const [isSavingProgress, setIsSavingProgress] = useState(false)
  const [progressSaveError, setProgressSaveError] = useState(false)
  const [isMarkingComplete, setIsMarkingComplete] = useState(false)

  const playerRef = useRef<MuxPlayerElement | null>(null)
  const progressSaveInterval = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (token && assignmentId && itemId) {
      loadData()
    }
    return () => {
      if (progressSaveInterval.current) {
        clearInterval(progressSaveInterval.current)
      }
    }
  }, [token, assignmentId, itemId])

  const loadData = async () => {
    if (!token || !assignmentId || !itemId) return
    setIsLoading(true)
    setError(null)
    try {
      // Validate CLIENT token (not assignment token)
      // The /c/:token routes use client access tokens
      const clientData = await clientService.validateClientAccessToken(token)
      if (!clientData) {
        setError('Invalid or expired access link')
        return
      }
      setClientId(clientData.client.id)

      // Get assignment with progress
      const data = await assignmentService.getAssignmentWithProgress(
        assignmentId,
        clientData.client.id
      )
      if (!data) {
        setError('Program not found')
        return
      }
      setAssignment(data)

      // Find current item
      const item = data.program.items.find((i) => i.id === itemId)
      if (!item) {
        setError('Exercise not found')
        return
      }
      setCurrentItem(item)
      setWatchedSeconds(item.progress?.watched_seconds || 0)
      setIsCompleted(!!item.progress?.completed_at)
    } catch (err) {
      console.error('Failed to load video:', err)
      setError('Failed to load exercise. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const saveProgress = useCallback(async () => {
    if (!assignmentId || !itemId || !clientId || !currentItem?.video_id) return
    if (isSavingProgress) return

    setIsSavingProgress(true)
    try {
      await assignmentService.updateProgress(assignmentId, itemId, {
        video_id: currentItem.video_id,
        client_id: clientId,
        watched_seconds: watchedSeconds,
      })
      setProgressSaveError(false)
    } catch (err) {
      console.error('Failed to save progress:', err)
      if (!progressSaveError) {
        setProgressSaveError(true)
        toast.warning('Having trouble saving progress. Use "Mark Complete" button when done.')
      }
    } finally {
      setIsSavingProgress(false)
    }
  }, [assignmentId, itemId, clientId, currentItem?.video_id, watchedSeconds, isSavingProgress, progressSaveError, toast])

  const handleTimeUpdate = (e: Event) => {
    const player = e.target as MuxPlayerElement
    if (!player) return
    const currentTime = Math.floor(player.currentTime || 0)
    setWatchedSeconds(currentTime)

    // Check for completion (90% watched)
    const duration = player.duration
    if (duration && currentTime >= duration * 0.9 && !isCompleted) {
      markCompleted()
    }
  }

  const markCompleted = async () => {
    if (!assignmentId || !itemId || !clientId || !currentItem?.video_id) return
    if (isCompleted || isMarkingComplete) return

    setIsMarkingComplete(true)
    try {
      await assignmentService.updateProgress(assignmentId, itemId, {
        video_id: currentItem.video_id,
        client_id: clientId,
        watched_seconds: watchedSeconds,
        completed: true,
      })
      setIsCompleted(true)
      setProgressSaveError(false)
      toast.success('Exercise completed!')
    } catch (err) {
      console.error('Failed to mark completed:', err)
      toast.error('Failed to mark as complete. Please try again.')
    } finally {
      setIsMarkingComplete(false)
    }
  }

  const handleVideoPlay = () => {
    // Save progress every 10 seconds while playing
    if (progressSaveInterval.current) {
      clearInterval(progressSaveInterval.current)
    }
    progressSaveInterval.current = setInterval(saveProgress, 10000)
  }

  const handleVideoPause = () => {
    if (progressSaveInterval.current) {
      clearInterval(progressSaveInterval.current)
    }
    saveProgress()
  }

  const handleVideoEnded = () => {
    if (progressSaveInterval.current) {
      clearInterval(progressSaveInterval.current)
    }
    if (!isCompleted) {
      markCompleted()
    }
  }

  const goToNextExercise = () => {
    if (!assignment || !currentItem) return
    const currentIndex = assignment.program.items.findIndex(
      (i) => i.id === currentItem.id
    )
    const nextItem = assignment.program.items[currentIndex + 1]
    if (nextItem) {
      navigate(`/c/${token}/video/${assignmentId}/${nextItem.id}`)
    } else {
      navigate(`/c/${token}/program/${assignmentId}`)
    }
  }

  const goToPrevExercise = () => {
    if (!assignment || !currentItem) return
    const currentIndex = assignment.program.items.findIndex(
      (i) => i.id === currentItem.id
    )
    const prevItem = assignment.program.items[currentIndex - 1]
    if (prevItem) {
      navigate(`/c/${token}/video/${assignmentId}/${prevItem.id}`)
    }
  }

  if (isLoading) {
    return <LoadingScreen message="Loading exercise..." />
  }

  if (error || !assignment || !currentItem) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <svg
            className="w-16 h-16 text-red-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Exercise Unavailable
          </h1>
          <p className="text-gray-600">{error || 'Unable to load this exercise.'}</p>
          <Link
            to={`/c/${token}/program/${assignmentId}`}
            className="mt-4 inline-block text-blue-600 hover:text-blue-800"
          >
            Back to Program
          </Link>
        </div>
      </div>
    )
  }

  const currentIndex = assignment.program.items.findIndex(
    (i) => i.id === currentItem.id
  )
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < assignment.program.items.length - 1

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-gray-800 text-white px-4 py-3 flex items-center justify-between">
        <Link
          to={`/c/${token}/program/${assignmentId}`}
          className="flex items-center text-gray-300 hover:text-white"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Program
        </Link>
        <span className="text-sm text-gray-400">
          Exercise {currentIndex + 1} of {assignment.program.items.length}
        </span>
        {isCompleted && (
          <span className="flex items-center gap-1 text-green-400 text-sm">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Completed
          </span>
        )}
      </header>

      {/* Video player */}
      <div className="flex-1 flex items-center justify-center bg-black relative px-4">
        {currentItem.video?.mux_playback_id ? (
          <div className="w-full max-w-5xl aspect-video relative">
            <MuxPlayer
              ref={playerRef}
              playbackId={currentItem.video.mux_playback_id}
              metadata={{
                video_id: currentItem.video.id,
                video_title: currentItem.video.title,
              }}
              streamType="on-demand"
              autoPlay
              accentColor={muxConfig.playback.accentColor}
              primaryColor={muxConfig.playback.primaryColor}
              onTimeUpdate={handleTimeUpdate}
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
              onEnded={handleVideoEnded}
              style={{ width: '100%', height: '100%' }}
            />
            {/* Watermark Overlay */}
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 pointer-events-none select-none">
              <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                <img
                  src="/logo-icon.png"
                  alt=""
                  className="w-5 h-5 object-contain opacity-70"
                />
                <span className="text-white/70 text-sm font-semibold tracking-wide">
                  ExerciseToolbox
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-400">
            <svg
              className="w-24 h-24 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <p>Video not available</p>
          </div>
        )}
      </div>

      {/* Exercise details */}
      <div className="bg-gray-800 text-white px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-xl font-bold mb-2">
            {currentItem.video?.title || 'Exercise'}
          </h1>

          {/* Exercise parameters */}
          <div className="flex flex-wrap gap-4 text-sm text-gray-300 mb-4">
            {currentItem.sets && (
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
                {currentItem.sets} sets
              </span>
            )}
            {currentItem.reps && (
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                  />
                </svg>
                {currentItem.reps} reps
              </span>
            )}
            {currentItem.duration_seconds && (
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {Math.floor(currentItem.duration_seconds / 60)} min
              </span>
            )}
            {currentItem.rest_seconds && (
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
                {currentItem.rest_seconds}s rest
              </span>
            )}
          </div>

          {/* Instructions */}
          {currentItem.notes && (
            <div className="bg-gray-700 rounded-lg p-4 mb-4">
              <h3 className="text-sm font-medium text-gray-300 mb-1">
                Instructions
              </h3>
              <p className="text-gray-100">{currentItem.notes}</p>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-700">
            <Button
              variant="outline"
              onClick={goToPrevExercise}
              disabled={!hasPrev}
              className={!hasPrev ? 'opacity-50 cursor-not-allowed' : ''}
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Previous
            </Button>

            {!isCompleted && (
              <Button
                onClick={markCompleted}
                isLoading={isMarkingComplete}
                disabled={isMarkingComplete}
              >
                {progressSaveError && (
                  <svg className="w-4 h-4 mr-1 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                Mark Complete
              </Button>
            )}

            <Button variant="outline" onClick={goToNextExercise}>
              {hasNext ? 'Next' : 'Finish'}
              <svg
                className="w-4 h-4 ml-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
